import VueI18n from 'vue-i18n';
import { Route } from 'vue-router';
import {
	Template,
	PresetField,
	CustomField,
	Plan,
	Contact,
	StripeProduct,
	ContactProfileDto,
	Properties,
	ContactDictionary,
	ContactTypeCode,
	Form,
	AuditLog,
	FamilyRelation,
	Center,
	SubmitForm,
	Payment,
	Receipt,
	MultiLangText,
	LocaleCode,
	Cause,
	Recipient,
	Sdgoal,
	ProfileType,
	ContactProfile,
	ContactDto,
	UserAccount,
	RelationCode,
	FormTypeItem,
	QRCode,
	TableItemsOptions,
	Transaction,
	Ngo,
	ContactProfileStatusCode,
	JobEventType,
	Job,
	JobDto,
	JobSessionType,
	JobEditStep,
	JobApplication,
	JobStatusCode,
	AddressItem,
	JobTimeslot,
	JobAttendance,
	JobSession,
	UserRoleTemplate,
	UserRole,
	UserRoleDto,
	UserPermission,
	PlanPaymentMethod,
	HeaderPreference,
	ConnectInvite,
	JobTimeslotInstance,
	JobGroupedAttendanceChanges,
	AppUserProfile,
	JobApplicationDto,
	CreateAppUserDto,
	JobAttendanceImportTableItem,
	AppBanner,
	VolunteerFunction,
	SubgroupedContactProfiles,
	JobApplicationCache,
	Funding,
	ProgramFormMode,
	ProgramEditStep,
	Program,
	ProgramReport,
	ProgramEvent,
	ProgramDto,
	ProgramValidations,
	JobValidations,
	TagType,
	TagValue,
	TagValueItem,
	ProgramFunderReport,
	ProgramListType,
	ReportTypeCode,
	ReportTypeStatusCode,
	Proposal,
	ProgramFormValidations,
	ProgramFormData,
	ProgramFormTemplate,
	FormApproval,
	ProgramMode,
	AdvancePaymentRequest,
	Evaluation,
	Breadcrumb,
	AddPartner,
	ViewPartner,
	MembershipApplication,
	ProposalCreateDto,
	Funder,
	ActivityTheme,
	AuditLogIndex,
	CaseNote,
	PaymentUserCategory,
} from '../../types';
import {
	EventEditStep,
	Event,
	EventActivityType,
	EventActivityTheme,
	EventServiceType,
	EventTargetAudience,
	EventOption,
	EventStatusCode,
	EventApplication,
	EventScheduleInstance,
	EventAttendance,
	EventTicketItem,
	EventAttendanceImportTableItem,
	EventApplicationDto,
	EventValidations,
	EventDto,
	EventApplicationCache,
} from '../../types/event';
import { FilterData } from '../../types/filter';
import { District, Region, SubDistrict } from '../../types/location';
import { NgoSignupBasicDto, NgoSignupExtraDto } from '../../types/ngo-signup';

export enum SaveAction {
	draft = 'draft',
	publish = 'publish',
	submit = 'submit',
}
export interface RootState {
	userID: number;
	isMiniDrawer: boolean;
	isMobileDrawer: boolean;
	useDummyData: boolean;
}

export enum AuthStatus {
	loading = 'loading',
	success = 'success',
	error = 'error',
}

export interface AuthState {
	status: AuthStatus | null;
	sessionExpiredDialog: boolean;
	sessionExpiredCheckingPaused: boolean;
	scheduledRefreshTimeoutHandler: NodeJS.Timeout | null;
	accessToken: string | null;
}

export interface PaymentsData {
	items: {
		data: Array<Payment>;
		status?: string;
	};
	total: number;
}

export interface TransactionsData {
	items: {
		data: Array<Payment>;
		status?: string;
	};
	total: number;
}
export interface CashTransactionData {
	items: {
		data: Array<Transaction>;
		amounts: [];
		status?: string;
	};
	total: number;
}

export interface PaymentConstantValues {
	code: string;
	name: MultiLangText;
}

export interface PaymentMethodValues extends PaymentConstantValues {
	type: PlanPaymentMethod;
}

export interface PaymentState {
	loading: number;
	listForContacts: number[];
	listForUserCategory: PaymentUserCategory | null;
	currentPayment: Payment | null;
	currentReceipt: Receipt | null;
	receiptDialogOpen: boolean;
	paymentsTableData: PaymentsData;
	transactionsTableData: TransactionsData;
	cashBalanceTableData: CashTransactionData;
	lastError?: string;
	paymentStatuses: PaymentConstantValues[];
	paymentMethods: PaymentMethodValues[];
	accountsMissing: boolean;
	accountsChecked: boolean;
}

export interface PlanTableData {
	items: {
		data: Array<Plan>;
		status?: string;
	};
	total: number;
}
export interface StripeProductTableData {
	items: Array<StripeProduct>;
	total: number;
}
export interface PriceState {
	loading: number;
	plans: Plan[];
	planTableData: PlanTableData;
	stripeProductTableData: StripeProductTableData;
}

export interface InboxMessage {
	id: string;
	userId: string;
	message: MultiLangText;
	data?: { [key: string]: string };
	readAt?: Date;
	archivedAt?: Date;
	createdAt: Date;
}

export interface ProfileBasic {
	ngoID: number;
	centerId: number;
	email: string;
	name: string;
	staffID: string;
	userCode: string;
	accessCenters: Center[] | null; // null means not loaded (unkown) yet
	onboarding?: boolean;
	isSignupUser?: boolean;
	roleCode: string;
}

export interface ProfilePreferences {
	language: LocaleCode;
	dateFormat: string;
	twoFA: boolean;
	saveTableColumnSettings: boolean;
	calendarFormat: 'standard' | 'custom';
	tableColumnSettings: { [key: string]: HeaderPreference[] };
}

export interface ProfileState {
	loading: boolean;
	lastError: string;
	basic: ProfileBasic;
	unreadInboxMessages: InboxMessage[];
	unreadInboxMessageTotal: number;
	preferences: ProfilePreferences;
	ngo: Ngo | null;
}

export interface AppState {
	urlQueryLang?: LocaleCode;
	preferenceLang?: LocaleCode;
	previousPage?: Route;
	redirectTo?: string;
	breadcrumbsItems: BreadcrumbsItem[];
	versionUpdateDialog: boolean;
	isInitialVersionCheck: boolean;
	versionCheckedTimestamp: Date;
	appBanners: Array<AppBanner> | null;
	currentAppBanner: AppBanner | null;
	loading: number;
	lastError: string;
	previousBreadcrumbs: Breadcrumb[];
}

export interface VolunteerState {
	currentAppUser?: AppUserProfile;
	currentDraftAppUser?: CreateAppUserDto;
}

export interface BreadcrumbsItem {
	to?: string;
	text: VueI18n.TranslateResult;
	exact: boolean;
}

export interface AlertState {
	message: AlertMessage | null;
}

export interface AlertMessage {
	text: string | string[];
	type: 'success' | 'error' | 'warning';
}

export enum ContactEditableItem {
	contactRemarks = 'contactRemarks',
	contact = 'contact',
	contactProfile = 'contactProfile',
	newContactProfile = 'newContactProfile',
	familyContacts = 'familyContacts',
	familyContact = 'familyContact',
	membershipRenew = 'membershipRenew',
	newFamilyMember = 'newFamilyMember',
	familyMembers = 'familyMembers',
}

export interface DictionaryFieldsWithGroup {
	fields: Properties[];
	groupName?: MultiLangText;
}

export interface DraftFamilyContactWithRelation {
	contact: ContactDto;
	relation?: RelationCode;
}

export interface ContactProfileData {
	items: Array<ContactProfile>;
	total: number;
}

export interface ApplicationData {
	items: Array<MembershipApplication>;
	total: number;
}

export interface ContactData {
	items: Array<Contact>;
	total: number;
}

export interface PartnerPageData {
	items: Array<ViewPartner>;
	total: number;
}

export interface NgoPageData {
	items: Array<Ngo>;
	total: number;
}

export interface AddPartnerPageData {
	items: Array<AddPartner>;
	total: number;
}

export interface ContactConstantValues {
	code: string;
	name: MultiLangText;
}

export interface ContactOwner {
	ownerAllCenters: boolean;
	centerIds: number[];
}

export interface ContactState {
	loading: number;
	contact: Contact | null;
	contacts: Contact[];
	selectedContacts: Contact[];
	mergeTargetContact: Contact | null;
	searchMergeContactBy: SearchMergeContactBy;
	searchMergeContactInput: string | null;
	currentContact?: Contact;
	currentContactProfile: ContactProfileDto | null;
	caseNotes?: CaseNote[];
	currentMembershipApplication: MembershipApplication | null;
	draftContact: ContactDto | null;
	draftContactProfileStatus: ContactProfileStatusCode | null;
	draftContactProfilePaymentStatus: string | null;
	profileCenters: Center[] | null;
	profileUsers: UserAccount[] | null;
	pendingDraftContactProfilePaymentStatus: {
		status: string;
		currentProfile: ContactProfile;
	}[];
	draftFamilyContactsWithRelations: DraftFamilyContactWithRelation[];
	draftFamilyMembers: { [key: number]: ContactProfileDto };
	editingItem: ContactEditableItem | null;
	familyRelations: FamilyRelation[];
	familyRelationsByContact: {
		[contactId: number]: FamilyRelation[];
	};
	currentCenter: Center | null;
	profileTypes: ProfileType[];
	currentContactType: ContactTypeCode | null;
	currentProfileType: ProfileType | null;
	familyMemberProfiles: ContactProfile[];
	allFamilyProfilesAllMembers: ContactProfile[];
	duplicateContactChecking: {
		contacts: Contact[];
		criteria: Partial<Contact>;
	};
	duplicateFamilyContactCheckings: {
		contacts: Contact[];
		criteria: Partial<Contact>;
	}[];
	contactPaymentStatuses: ContactConstantValues[];
	applicationsForContacts: number[];
	membersTableData: ContactProfileData;
	volunteersTableData: ContactProfileData;
	participantsTableData: ContactProfileData;
	jobParticipantsTableData: ContactProfileData;
	allContactsTableData: ContactData;
	applicationsTableData: ContactProfileData;
	mergeContactsTableData: ContactData;
	partnerTableData: PartnerPageData;
	ngosForPartership: NgoPageData;
	allPartners: ViewPartner[];
	allPartnersUnitMap: AllPartnersUnitMap;
	contactSearchResult: Contact[]; // for job & event application
	connectInvite?: ConnectInvite;
	caseProfileDialogOpen: boolean;
	donationProfileDialogOpen: boolean;
}

export interface AllPartnersUnitMap {
	[key: string]: Partial<Center>[];
}

export enum SearchMergeContactBy {
	name = 'name',
	email = 'email',
	phone = 'phone',
}

export interface DictState {
	profileDictionaries: {
		[contactType in ContactTypeCode]: ContactDictionary[];
	};
	contactDictionaries: ContactDictionary[];
}

export interface TemplateData {
	items: Array<Template>;
	total: number;
}

export interface PublicFormData {
	items: Array<Form>;
	total: number;
}

export interface FormState {
	loading: number;
	lastError?: string;
	isEditing: boolean;
	publicUrl: string;
	templates: Template[];
	currentTemplate?: Template;
	currentSubmitForm: SubmitForm | null;
	basicFields: Properties[];
	basicFieldsType: string[];
	presetFields: PresetField[];
	customFields: CustomField[];
	profileDictionaries: {
		[contactType in ContactTypeCode]: ContactDictionary[];
	};
	contactDictionaries: ContactDictionary[];
	forms: Form[];
	formTypes: FormTypeItem[];
	planEditing: boolean;
	draftTableData: TemplateData;
	submittedTableData: TemplateData;
	formTableData: PublicFormData;
	cloneFields: {};
}

export interface AuditPageData {
	items: Array<AuditLogIndex>;
	total: number;
}

export interface AuditState {
	pageData: AuditPageData;
	loading: number;
	lastError?: string;
	details?: AuditLog;
}

export interface QRCodeData {
	items: Array<QRCode>;
	total: number;
}
export interface QRCodeState {
	pageData: QRCodeData;
	loading: number;
	lastError?: string;
}

export interface UserPageData {
	items: Array<UserAccount>;
	total: number;
}

export interface EditingUser
	extends Partial<
		Pick<
			UserAccount,
			'id' | 'name' | 'jobTitle' | 'email' | 'phone' | 'ngoId' | 'centerId'
		>
	> {
	roleId?: string;
	password?: string;
	accessCenterIds?: number[];
	privateRole?: Pick<
		UserRoleDto,
		'name' | 'permissionIds' | 'baseRoleId'
	> | null;
}

export interface UserState {
	pageData: UserPageData;
	loading: number;
	lastError?: string;
	users?: UserAccount[];
	usersWithDeleted?: UserAccount[];
	editingUser?: EditingUser;
	editingUserPrivateRoleId?: string;
	userTemplates: UserRoleTemplate[];
	userRoles?: UserRole[];
	editingUserRole: UserRoleDto | null;
	userPermissions: UserPermission[];
	permissionsEditing: boolean;
}

export interface UnitState {
	loading: boolean;
	lastError?: string;
	centers?: Center[];
	causes?: Cause[];
	recipients?: Recipient[];
	sdgoals?: Sdgoal[];
	unitsInfo?: { result: Center[]; total: number };
	activityThemes?: ActivityTheme[];
}

export interface FilterState {
	loading: boolean;
	lastError?: string;
	filter: FilterData[];
}

type BasicContactProfileGroup = Exclude<
	ContactTypeCode,
	ContactTypeCode.participant | ContactTypeCode.jobParticipant
>;

const {
	participant,
	jobParticipant,
	...BasicContactProfileGroup
} = ContactTypeCode;

enum SpecialContactProfileGroup {
	// job/event participant
	activity = 'activity',
}

export type ContactProfileGroup =
	| BasicContactProfileGroup
	| SpecialContactProfileGroup;

export const ContactProfileGroup = {
	...BasicContactProfileGroup,
	...SpecialContactProfileGroup,
};

export interface GroupedContactProfiles {
	groupKey: ContactProfileGroup;
	subgroupedContactProfiles?: SubgroupedContactProfiles[];
}

export interface NotificationState {
	lastError?: string;
	notification: { [groupKey: string]: { [itemKey: string]: number } };
	notificationTimer?: number;
	onboarding?: { pendingSteps: number };
}

export interface TableState {
	exportLoading: boolean;
	tableOptions: { [key: string]: TableItemsOptions };
}

export interface NgoPageData {
	items: Array<Ngo>;
	total: number;
}

export interface NgoState {
	pageData: NgoPageData;
	loading: boolean;
	lastError?: string;
}

export enum JobListType {
	current = 'current',
	draft = 'draft',
	all = 'all',
	serviceHours = 'serviceHours',
	programEvent = 'programEvent',
}

export enum JobProgressCode {
	ongoing = 'ongoing',
	completed = 'completed',
}

export const JobStatusesInListType: {
	[key in JobListType]: JobStatusCode[];
} = {
	[JobListType.current]: [JobStatusCode.published],
	[JobListType.draft]: [JobStatusCode.draft],
	[JobListType.all]: [
		JobStatusCode.draft,
		JobStatusCode.published,
		JobStatusCode.completed,
		JobStatusCode.closed,
		JobStatusCode.cancelled,
		JobStatusCode.full,
	],
	[JobListType.serviceHours]: [
		JobStatusCode.published,
		JobStatusCode.completed,
		JobStatusCode.closed,
	],
	[JobListType.programEvent]: [
		JobStatusCode.published,
		JobStatusCode.completed,
		JobStatusCode.closed,
		JobStatusCode.full,
	],
};

export interface JobPageData {
	items: Array<Job>;
	total: number;
}

export interface JobTimeslotPageData {
	items: Array<JobTimeslot>;
	total: number;
}

export interface JobState {
	eventTypes: JobEventType[];
	volunteerFunctions: VolunteerFunction[];
	sessionTypes: JobSessionType[];
	loading: number;
	submitting: boolean;
	submitFrom: SaveAction | null;
	lastError?: string;
	currentJob: Job | JobDto | null;
	currentJobValidations: JobValidations;
	currentStep: JobEditStep;
	applicationsPageData?: JobApplicationPageData;
	currentApplication: JobApplication | null;
	currentDraftApplication?: JobApplicationDto;
	pageData: {
		[key: string]: JobPageData;
	};
	jobTimeslotPageData: {
		[key: string]: JobTimeslotPageData;
	};
	currentTimeslot?: JobTimeslot;
	currentServiceHoursSession?: JobSession;
	currentTimeslotInstance?: JobTimeslotInstance;
	lastJobList: string;
	attendanceOfflineFullList: {
		[timeslotInstanceId: string]: JobAttendance[];
	};
	attendanceFullList: JobAttendance[];
	groupedOfflineAttendanceChanges: JobGroupedAttendanceChanges;
	localStorageEventListenerFunction?: (
		this: Window,
		ev: WindowEventMap['storage'],
	) => any;
	localStorageEventListenerCount: number;
	attendanceImportData: JobAttendanceImportTableItem[];
	applicationCache: JobApplicationCache;
}

export interface JobApplicationPageData {
	items: Array<JobApplication>;
	total: number;
}

export interface EventApplicationPageData {
	items: Array<EventApplication>;
	total: number;
}

export interface EventScheduleInstancePageData {
	items: EventScheduleInstance[];
	total: number;
}

export interface EventAttendancePageData {
	items: Array<EventAttendance>;
	total: number;
}

export enum ApplicationPaneKey {
	timeslotsPane = 'timeslotsPane',
	eventDetailsPane = 'eventDetailsPane',
	jobDetailsPane = 'jobDetailsPane',
}
export interface ApplicationPaneState {
	pane?: ApplicationPaneKey;
	loading: boolean;
	lastError?: string;
	isPaneOpen: boolean;
	paneCloseTimeout?: number;
}

export enum CaseNotePaneKey {
	caseNoteEditPane = 'caseNoteEditPane',
}
export interface CaseNotePaneState {
	pane?: CaseNotePaneKey;
	loading: boolean;
	lastError?: string;
	isPaneOpen: boolean;
	isEditing: boolean;
	canEditNote: boolean;
	paneCloseTimeout?: number;
	profileId?: number;
	noteId?: string;
}

export interface NgoSignupState {
	basicData: Partial<NgoSignupBasicDto>;
	extraData: Partial<NgoSignupExtraDto>;
	currentStep: number;
}

export interface LocationState {
	regions: Region[];
	districts: District[];
	subDistricts: SubDistrict[];
	availableAddresses: AddressItem[];
}

export interface PrintFrameState {
	delayedPrinting: boolean;
	printSource: any;
	mimeType: string;
}

export enum EventListType {
	current = 'current',
	draft = 'draft',
	all = 'all',
	attendance = 'attendance',
	ticketing = 'ticketing',
	programEvent = 'programEvent',
}

export const EventStatusesInListType: {
	[key in EventListType]: EventStatusCode[];
} = {
	[EventListType.current]: [
		EventStatusCode.published,
		EventStatusCode.full,
		EventStatusCode.closed,
	],
	[EventListType.draft]: [EventStatusCode.draft],
	[EventListType.all]: [
		EventStatusCode.draft,
		EventStatusCode.published,
		EventStatusCode.completed,
		EventStatusCode.closed,
		EventStatusCode.cancelled,
		EventStatusCode.full,
	],
	[EventListType.attendance]: [
		EventStatusCode.published,
		EventStatusCode.completed,
	],
	[EventListType.ticketing]: [EventStatusCode.published],
	[EventListType.programEvent]: [
		EventStatusCode.published,
		EventStatusCode.completed,
		EventStatusCode.closed,
		EventStatusCode.full,
	],
};

export interface EventPageData {
	items: Array<Event>;
	total: number;
}

export interface EventCheckIn {
	id: string;
	time: Date;
}

export interface EventTicketPageData {
	items: Array<EventTicketItem>;
	total: number;
}

export interface EventState {
	loading: 0;
	submitting: boolean;
	submitFrom: SaveAction | null;
	lastError?: string;
	currentEvent: Event | EventDto | null;
	currentEventValidations: EventValidations;
	currentStep: EventEditStep;
	lastEventList: EventListType;
	activityTypes: EventActivityType[];
	activityThemes: EventActivityTheme[];
	serviceTypes: EventServiceType[];
	targetAudiences: EventTargetAudience[];
	currentEventOptions: EventOption[];
	pageData: {
		[key in EventListType]?: EventPageData;
	};
	currentApplication: EventApplication | null;
	currentDraftApplication?: EventApplicationDto;
	applicationsPageData: EventApplicationPageData | null;
	scheduleInstancePageData: {
		[key: string]: EventScheduleInstancePageData;
	};
	currentAttendanceOption: EventOption | null;
	currentScheduleInstance: EventScheduleInstance | null;
	attendancePageData: {
		[key: string]: EventAttendancePageData;
	};
	attendanceFullData: {
		[key: string]: EventAttendancePageData;
	};
	attendanceOfflineFullList: { [key: string]: EventPageData };
	attendanceFullList: EventPageData;
	offlineCheckins: { [key: string]: EventCheckIn[] };
	ticketPageData: { [key: string]: EventTicketPageData };
	localStorageEventListenerFunction?: (
		this: Window,
		ev: WindowEventMap['storage'],
	) => any;
	localStorageEventListenerCount: number;
	attendanceImportData: EventAttendanceImportTableItem[];
	applicationCache: EventApplicationCache;
}

export interface ProgramPageData {
	items: Array<ProgramDto>;
	total: number;
}

export interface ReportsPageData {
	items: Array<ProgramReport>;
	total: number;
}

export interface EventsPageData {
	items: Array<ProgramEvent>;
	total: number;
}

export interface FunderReportPageData {
	items: Array<ProgramFunderReport>;
	total: number;
}

export interface ProposalAndEvaluationPageData {
	items: Array<Proposal>;
	total: number;
}

export interface AdvancePaymentRequestPageData {
	items: Array<AdvancePaymentRequest>;
	total: number;
}

export interface ProgramReportType {
	name: MultiLangText;
	reportTypeCode: ReportTypeCode;
	revisionName?: MultiLangText;
	revisionDate?: string;
	status: ReportTypeStatusCode;
}

export interface ProgramState {
	loading: 0;
	downloadDataLoading: 0;
	pageData: {
		[key in ProgramListType]?: ProgramPageData;
	};
	reportsPageData: ReportsPageData | null;
	eventsPageData: EventsPageData | null;
	funderReportsPageData: FunderReportPageData | null;
	proposalAndEvaluationPageData: ProposalAndEvaluationPageData | null;
	advancePaymentRequestPageData: AdvancePaymentRequestPageData | null;

	submitting: boolean;
	submitFrom: SaveAction | null;
	lastError?: string;
	currentProgramValidations: ProgramValidations;
	currentProgram: Program | null;
	currentProgramReport: ProgramReport | null;
	currentMode: ProgramMode;
	currentStep: ProgramEditStep;
	lastProgramList: ProgramListType;
	reportTypeRevisions: ProgramReportType[];
	funders: Funder[];
	fundings: Funding[];
	tagTypes: TagType[];
	tagTypesByReportTypeMap: {
		[key: string]: TagType[];
	};
	groupedValueItems: {
		[key: string]: {
			[key: string]: TagValueItem[];
		};
	};
	groupedSubValueItems: {
		[key: string]: {
			[key: string]: {
				[key: string]: TagValue[];
			};
		};
	};

	//program form
	programFormTemplates: ProgramFormTemplate[] | null;
	programFormTemplatesCombinations: { [key: string]: any } | null;
	currentProgramFormMode: ProgramFormMode;
	currentProgramFormStep: string;
	currentProgramFormValidations: ProgramFormValidations;
	currentProgramFormTemplate: ProgramFormTemplate | null;
	currentProgramFormData: ProgramFormData;
	currentProgramForm: FormApproval;

	//proposal
	currentProposal: Proposal | null;
	currentCloneProposal: ProposalCreateDto | null;

	//advance payment request
	currentAdvancePaymentRequest: AdvancePaymentRequest | null;

	//proposal
	currentEvaluation: Evaluation | null;

	/**
	 * Not best solution, dont assume it to be always the current login user ID
	 * Used to check specific user's permission for different program forms
	 */
	currentUserId: string;
}

export interface PeriodConstraints {
	inclusive: boolean;
	start?: Date;
	end?: Date;
}
